import { createSignal, onCleanup, Show, type VoidComponent } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { useTranslation } from "../../../../../state/i18n";
import SettingSection from "../../components/SettingSection";
import { TextButton } from "../../../../../components/button/Button.tsx";
import { isNil } from "lodash-es";
import { Translation } from "../../../../../utils/Translation.tsx";
import { keyFromSelector } from "i18next";
import TextLink from "../../../../../components/link/TextLink.tsx";
import { useModalManager } from "../../../../../components/modal/ModalManager.tsx";
import { WarningModal } from "../../../../../components/modal/Modal.tsx";
import CancelSubscriptionModal from "./CancelSubscriptionModal.tsx";
import UndoCancellationModal from "./UndoCancellationModal.tsx";
import type { SubscriptionPlanDto } from "../../../../../services/BillingService.ts";
import BillingService from "../../../../../services/BillingService.ts";
import { useCustomerState } from "../../../../../state/customer.tsx";
import GenericFailureModal from "../../../../GenericFailureModal.tsx";
import { seconds } from "../../../../../utils/timeUtil.ts";

type CancelSectionProps = {
  subscription?: SubscriptionPlanDto;
  cycleEnd?: number;
};

const CancelSection: VoidComponent<CancelSectionProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const navigate = useNavigate();
  const checkOpenInvoices = BillingService.hasOpenInvoices();
  const [isCheckingInvoices, setIsCheckingInvoices] = createSignal(false);

  // Navigating away during the invoice check must not open a modal on the next page
  let isDisposed = false;
  onCleanup(() => {
    isDisposed = true;
  });

  const startCancellation = async (): Promise<void> => {
    const subscriptionId = props.subscription?.subscription;
    const cycleEnd = props.cycleEnd;
    if (isNil(subscriptionId)) {
      return;
    }

    setIsCheckingInvoices(true);
    try {
      const hasOpenInvoices = await checkOpenInvoices();
      if (isDisposed) {
        return;
      }

      if (hasOpenInvoices) {
        open({
          content: () => (
            <WarningModal
              title={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_OPEN_INVOICES_MODAL_TITLE)}
              description={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_OPEN_INVOICES_MODAL_DESCRIPTION)}
              cancelLabel={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_OPEN_INVOICES_MODAL_ACTIONS_CANCEL)}
              confirmLabel={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_OPEN_INVOICES_MODAL_ACTIONS_CONFIRM)}
              onConfirm={() => {
                navigate(`/customers/${customer.customerId}/settings/invoices`);
              }}
            />
          ),
        });
      } else {
        open({
          content: () => <CancelSubscriptionModal subscriptionId={subscriptionId} cycleEnd={cycleEnd} />,
        });
      }
    } catch (error) {
      if (!isDisposed) {
        open({ content: () => <GenericFailureModal error={error} /> });
      }
    } finally {
      setIsCheckingInvoices(false);
    }
  };

  const openUndoCancellationModal = (): void => {
    const subscription = props.subscription;
    const subscriptionId = subscription?.subscription;
    const cycleEnd = props.cycleEnd;
    if (isNil(subscription) || isNil(subscriptionId)) {
      return;
    }

    open({
      content: () => (
        <UndoCancellationModal
          subscriptionId={subscriptionId}
          planName={subscription.name}
          interval={subscription.interval}
          nextInvoiceDate={isNil(cycleEnd) ? undefined : new Date(seconds(cycleEnd))}
        />
      ),
    });
  };

  return (
    <SettingSection
      ariaIdPrefix="cancel"
      title={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_TITLE)}
      description={
        <Translation
          key={keyFromSelector(($) => $.SETTINGS_BILLING_CANCEL_PLAN_DESCRIPTION)}
          components={{
            contactUsLink: (props) => <TextLink isExternal href={props.href} label={props.label} />,
          }}
        />
      }
    >
      <Show
        when={props.subscription?.cancelAtPeriodEnd}
        fallback={
          <TextButton
            text={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_CANCEL_BUTTON)}
            variant="secondary"
            color="destructive"
            class="ml-auto"
            disabled={isNil(props.subscription?.subscription)}
            loading={isCheckingInvoices()}
            onClick={() => {
              void startCancellation();
            }}
          />
        }
      >
        <TextButton
          text={t(($) => $.SETTINGS_BILLING_CANCEL_PLAN_UNDO_BUTTON)}
          variant="secondary"
          color="destructive"
          class="ml-auto"
          disabled={isNil(props.subscription?.subscription)}
          onClick={openUndoCancellationModal}
        />
      </Show>
    </SettingSection>
  );
};

export default CancelSection;
